import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  Search, 
  AlertTriangle, 
  CheckCircle2, 
  Share2, 
  Loader,
  ArrowLeft
} from "lucide-react";
import PageTitle from "./pageTitle";
import Button from "../common/Button";

const VerifyCertificate = () => {
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get("id") || "";
  
  const [searchId, setSearchId] = useState(idParam);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const triggerVerification = async (certId) => {
    if (!certId || !certId.trim()) return;
    setVerifying(true);
    setVerificationError(null);
    setVerificationResult(null);

    try {
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const response = await axios.get(`${baseUrl}/course/bk-verify-certificate/${certId.trim()}/`);
      setVerificationResult(response.data);
      showToast("Certificate authenticity successfully verified!", "success");
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.detail || "Certificate verification failed. Check the ID and try again.";
      setVerificationError(errMsg);
      showToast("Verification failed. Invalid Certificate ID.", "danger");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (idParam) {
      setSearchId(idParam);
      triggerVerification(idParam);
    }
  }, [idParam]);

  const handleVerifySubmit = (e) => {
    e.preventDefault();
    triggerVerification(searchId);
  };

  const showToast = (msg, type) => {
    setToastMessage({ message: msg, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  return (
    <>
      <Helmet>
        <title>Certificate Verification - Codehub Technologies</title>
        <meta name="description" content="Verify the cryptographic authenticity and validity of professional training certificates and career track credentials issued by Codehub Technologies." />
      </Helmet>

      <PageTitle title="Credential Registry" />

      {/* Main Container */}
      <div className="cht-min-h-[60vh] cht-py-16 cht-px-4 cht-bg-[#f2f4fd]/50">
        <div className="cht-max-w-3xl cht-mx-auto cht-space-y-10">
          
          {/* Header Description */}
          <div className="cht-text-center cht-space-y-3">
            <h2 className="cht-text-3xl cht-font-black cht-text-slate-900 cht-tracking-tight">
              Cryptographic Authenticator
            </h2>
            <p className="cht-text-sm cht-text-gray-500 cht-max-w-lg cht-mx-auto leading-relaxed cht-font-medium">
              Enter any unique certificate reference ID issued by Codehub Tech Studio to verify student graduate records, completed curricula, and clearance status instantly.
            </p>
          </div>

          {/* Search lookup card */}
          <div className="cht-bg-white cht-p-8 cht-rounded-[32px] cht-shadow-[0_12px_48px_rgba(0,0,0,0.03)] cht-border cht-border-gray-100">
            <form onSubmit={handleVerifySubmit} className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-4">
              <div className="cht-relative cht-flex-1">
                <input 
                  type="text" 
                  placeholder="Enter Certificate Reference ID (e.g. CH-1-CS-2026)" 
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  className="cht-w-full cht-bg-slate-50 cht-border cht-border-slate-200 focus:cht-border-[#3e57da] focus:cht-bg-white cht-h-14 cht-px-5 cht-pl-12 cht-rounded-2xl cht-text-sm cht-font-extrabold cht-placeholder-gray-400 focus:cht-outline-none focus:cht-ring-4 focus:cht-ring-[#3e57da]/5 transition-all"
                />
                <Search size={18} className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" />
              </div>
              
              <Button 
                type="submit" 
                variant="primary"
                size="lg"
                disabled={verifying}
                isLoading={verifying}
                leftIcon={<ShieldCheck size={18} />}
                className="cht-h-14 cht-px-8 cht-rounded-2xl"
              >
                Verify Authenticity
              </Button>
            </form>
          </div>

          {/* Verification Results Display */}
          <AnimatePresence mode="wait">
            {verificationResult && (
              <motion.div
                key="success-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="cht-bg-white cht-rounded-[36px] cht-p-8 sm:cht-p-10 cht-shadow-[0_24px_64px_rgba(0,0,0,0.04)] cht-border cht-border-emerald-100 cht-relative cht-overflow-hidden"
              >
                {/* Top emerald highlight bar */}
                <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-emerald-400 cht-via-teal-500 cht-to-indigo-500" />

                <div className="cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-6">
                  {/* Glowing Verified Badge */}
                  <div className="cht-w-20 cht-h-20 cht-bg-emerald-500/10 cht-text-emerald-600 cht-rounded-[28px] cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-inner cht-relative">
                    <div className="cht-absolute cht-inset-0 cht-rounded-[28px] cht-bg-emerald-500/10 cht-animate-ping cht-opacity-20" />
                    <ShieldCheck size={36} strokeWidth={1.8} />
                  </div>

                  <div className="cht-space-y-2">
                    <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3.5 cht-py-1.5 cht-rounded-full cht-bg-emerald-500/10 cht-text-[10px] cht-font-black cht-text-emerald-600 cht-uppercase cht-tracking-widest">
                      <CheckCircle2 size={12} /> {verificationResult.status}
                    </span>
                    <h3 className="cht-text-2xl cht-font-black cht-text-slate-900 cht-tracking-tight">
                      Codehub Tech Authenticated Credential
                    </h3>
                  </div>

                  {/* Details Card */}
                  <div className="cht-w-full cht-bg-slate-50 cht-p-6 sm:cht-p-8 cht-rounded-[24px] cht-border cht-border-slate-100/50 cht-space-y-4 cht-text-left">
                    <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100 pb-3">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Graduate Recipient</span>
                      <span className="cht-text-sm cht-font-black cht-text-slate-800">{verificationResult.student_name}</span>
                    </div>
                    <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100 pb-3">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Training Program</span>
                      <span className="cht-text-sm cht-font-black cht-text-slate-800 cht-text-right max-w-[280px] cht-line-clamp-2">{verificationResult.curriculum_title}</span>
                    </div>
                    <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100 pb-3">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Credential Type</span>
                      <span className="cht-text-xs cht-font-extrabold cht-text-slate-800">{verificationResult.curriculum_type}</span>
                    </div>
                    <div className="cht-flex cht-justify-between cht-items-baseline cht-border-b cht-border-slate-100 pb-3">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Issue Date</span>
                      <span className="cht-text-xs cht-font-extrabold cht-text-slate-800">{verificationResult.issue_date}</span>
                    </div>
                    <div className="cht-flex cht-justify-between cht-items-baseline">
                      <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Verification ID</span>
                      <span className="cht-text-xs cht-font-mono cht-font-black cht-text-[#3e57da]">{verificationResult.certificate_id}</span>
                    </div>
                  </div>

                  {/* Share CTA */}
                  <Button 
                    variant="secondary"
                    size="md"
                    fullWidth
                    leftIcon={<Share2 size={14} />}
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/verify-certificate?id=${verificationResult.certificate_id}`);
                      showToast("Cryptographic verification link copied!", "success");
                    }}
                    className="cht-h-12"
                  >
                    Copy Shareable Verification Link
                  </Button>
                </div>
              </motion.div>
            )}

            {verificationError && (
              <motion.div
                key="error-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="cht-bg-white cht-rounded-[36px] cht-p-8 sm:cht-p-10 cht-shadow-[0_24px_64px_rgba(0,0,0,0.04)] cht-border cht-border-rose-100 cht-relative cht-overflow-hidden"
              >
                {/* Top rose highlight bar */}
                <div className="cht-absolute cht-top-0 cht-left-0 cht-right-0 cht-h-1.5 cht-bg-gradient-to-r cht-from-amber-500 cht-via-orange-600 cht-to-rose-600" />

                <div className="cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-6">
                  {/* Warning Icon */}
                  <div className="cht-w-20 cht-h-20 cht-bg-rose-500/10 cht-text-rose-600 cht-rounded-[28px] cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-inner cht-relative">
                    <div className="cht-absolute cht-inset-0 cht-rounded-[28px] cht-bg-rose-500/10 cht-animate-ping cht-opacity-20" />
                    <AlertTriangle size={36} strokeWidth={1.8} />
                  </div>

                  <div className="cht-space-y-2">
                    <span className="cht-inline-flex cht-items-center cht-px-3.5 cht-py-1.5 cht-rounded-full cht-bg-rose-500/10 cht-text-[10px] cht-font-black cht-text-rose-600 cht-uppercase cht-tracking-widest">
                      Verification Failed
                    </span>
                    <h3 className="cht-text-2xl cht-font-black cht-text-slate-900 cht-tracking-tight">
                      Credential ID Not Found
                    </h3>
                  </div>

                  <div className="cht-w-full cht-bg-rose-50/50 cht-p-6 sm:cht-p-8 cht-rounded-[24px] cht-border cht-border-rose-100/50 cht-text-left">
                    <p className="cht-text-[10px] cht-font-black cht-text-rose-600 cht-uppercase cht-tracking-widest">Registry Report</p>
                    <p className="cht-text-xs cht-text-slate-600 cht-font-bold cht-mt-2 leading-relaxed">
                      {verificationError}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back button redirect */}
          <div className="cht-text-center">
            <Link to="/" className="cht-inline-flex cht-items-center cht-gap-2 cht-text-xs cht-font-black cht-text-slate-400 hover:cht-text-[#3e57da] cht-uppercase cht-tracking-wider transition-colors">
              <ArrowLeft size={14} />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Embedded Simple Custom Toast Notifications */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            className={`cht-fixed cht-bottom-8 cht-right-8 cht-z-[10000] cht-px-6 cht-py-4.5 cht-rounded-[20px] cht-shadow-xl cht-text-white cht-text-xs cht-font-bold ${
              toastMessage.type === "success" ? "cht-bg-emerald-600" : "cht-bg-rose-600"
            }`}
          >
            {toastMessage.message}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VerifyCertificate;
